import { useState, useCallback } from 'react';
import { useEventListener } from '@shopify/polaris';
import { useDebouncedCallback } from 'use-debounce';
import { useResizeObserver } from '../useResizeObserver/useResizeObserver.mjs';

const defaultOptions = {
  tolerance: 10,
  throttle: 5,
  observeAxes: {
    vertical: true,
    horizontal: false
  }
};
const defaultState = {
  canScrollUp: false,
  canScrollDown: false,
  canScrollLeft: false,
  canScrollRight: false
};

// For each direction (up, down, left, right)
// returns whether a given element can be scrolled in that direction
function useScrollOverflow(element, options) {
  var _ref;
  const {
    tolerance = defaultOptions.tolerance,
    throttle = defaultOptions.throttle
  } = options != null ? options : {};
  const {
    horizontal = defaultOptions.observeAxes.horizontal,
    vertical = defaultOptions.observeAxes.vertical
  } = (_ref = options && options.observeAxes) != null ? _ref : {};
  const [scrollOverflow, setScrollOverflow] = useState(defaultState);
  const handleResize = useCallback(() => {
    if (!element) {
      return;
    }
    setScrollOverflow(currentScrollOverflow => {
      const verticalOverflow = vertical && {
        canScrollUp: element.scrollTop > tolerance,
        canScrollDown: element.scrollHeight - element.clientHeight - element.scrollTop > tolerance
      };
      const horizontalOverflow = horizontal && {
        canScrollLeft: element.scrollLeft > tolerance,
        canScrollRight: element.scrollWidth - element.clientWidth - element.scrollLeft > tolerance
      };
      const newScrollOverflow = {
        ...defaultState,
        ...verticalOverflow,
        ...horizontalOverflow
      };
      if (isEqual(currentScrollOverflow, newScrollOverflow)) {
        return currentScrollOverflow;
      }
      return newScrollOverflow;
    });
  }, [element, horizontal, tolerance, vertical]);
  const handleScroll = useDebouncedCallback(handleResize, throttle, {
    leading: true
  });
  useResizeObserver(element, handleResize);
  useEventListener('scroll', handleScroll, element || undefined, {
    passive: true
  });
  return scrollOverflow;
}
function isEqual(first, second) {
  return first.canScrollUp === second.canScrollUp && first.canScrollDown === second.canScrollDown && first.canScrollLeft === second.canScrollLeft && first.canScrollRight === second.canScrollRight;
}

export { defaultOptions, useScrollOverflow };
